import { cva, VariantProps } from 'class-variance-authority';
import { HTMLAttributes } from 'react';
import { cn } from '~/lib/cn';

const badgeClassNames = cva(
    'tw-inline-block tw-rounded-xl tw-font-semibold tw-border-solid tw-border before:tw-content-[""] before:tw-align-middle before:tw-h-full before:tw-inline-block',
    {
        variants: {
            flat: {
                true: ''
            },
            color: {
                currentColor: 'tw-bg-current tw-text-current',
                warning: '',
                success: '',
                error: '',
                light: '',
                secondary: 'tw-bg-accents-6 tw-text-accents-1 tw-border-accents-7',
                beta: 'tw-bg-purple-600 tw-text-white'
            },
            size: {
                xs: 'tw-text-[10px] tw-leading-[13px] tw-line tw-px-1.5 tw-py-0.5',
                sm: 'tw-text-sm tw-px-3 tw-py-0.5',
                md: 'tw-text-md tw-px-3 tw-py-0.5',
                lg: 'tw-text-lg tw-px-3 tw-py-0.5',
                xl: 'tw-text-xl tw-px-3 tw-py-0.5'
            }
        },
        compoundVariants: [
            { flat: false, color: 'warning', className: 'tw-bg-warning/90 tw-text-white tw-border-warning' },
            { flat: false, color: 'success', className: 'tw-bg-success/90 tw-text-white tw-border-success' },
            { flat: false, color: 'error', className: 'tw-bg-error/90 tw-text-white tw-border-error' },
            { flat: false, color: 'light', className: 'tw-bg-accents-1 tw-text-accents-8 tw-border-accents-2' },
            { flat: false, color: 'beta', className: 'tw-bg-purple-600 tw-text-white tw-border-purple-900' },
            { flat: true, color: 'warning', className: 'tw-bg-warning/20 tw-text-warning tw-border-warning' },
            { flat: true, color: 'success', className: 'tw-bg-success/20 tw-text-success tw-border-success' },
            { flat: true, color: 'error', className: 'tw-bg-error/20 tw-text-error tw-border-error' },
            { flat: true, color: 'light', className: 'tw-bg-accents-1/50 tw-text-accents-8 tw-border-accents-2' }
        ],
        defaultVariants: {
            color: 'success',
            flat: false,
            size: 'sm'
        }
    }
);

type badgeVariants = VariantProps<typeof badgeClassNames>;

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, keyof badgeVariants>, badgeVariants {
    /** @deprecated */
    css?: any;
}

export default function Badge({ children, css, size, flat, className, color, ...props }: BadgeProps) {
    // `cn` rather than passing `className` into cva: cva only concatenates, so a caller's
    // colour or size lost to the variant it was meant to replace and had to be forced
    // through with `!important`. Merging lets the caller's class win on its own.
    return (
        <span className={cn(badgeClassNames({ flat, size, color }), className)} {...props}>
            {children}
        </span>
    );
}
